<template>
    <GeneralDialogTemplate @close="closeDialog">
        <template slot="header">
            <span class="font-weight-medium">
                {{ $t("Integration configuration") }}
            </span>
        </template>
        <v-row
            no-gutters
            class="mt-3"
        >
            <v-col
                class="px-1"
                cols="12"
            >
                <v-select
                    v-model="form_data.feature_id"
                    :label="$t('Integration')"
                    :items="integrations"
                    item-value="feature_id"
                    item-text="title"
                    outlined
                    :error-messages="form_data.feature_id_error"
                    menu-props="offsetY"
                    @change="integration_selected"
                ></v-select>
            </v-col>
            <v-col
                v-if="canFillData && show_country_input"
                class="px-1"
                cols="12"
            >
                <v-select
                    v-model="form_data.api_country"
                    :label="$t('Country')"
                    :placeholder="$t('Please choose your country')"
                    :items="merit_aktiva_api_countries"
                    :item-text="(x)=>$t(x.text)"
                    item-value="value"
                    outlined
                    menu-props="offsetY"
                ></v-select>
            </v-col>
            <v-col
                v-if="canFillData"
                class="px-1"
                cols="12"
            >
                <v-text-field
                    v-model.trim="form_data.api_key"
                    :label="get_field_translations('api_key')"
                    :error-messages="validation_errors.api_key_error"
                    outlined
                    :append-icon="api_key_visibility ? 'visibility' : 'visibility_off'"
                    :type="api_key_visibility ? 'password' : 'text'"
                    class="pr-1"
                    @click:append="() => (api_key_visibility = !api_key_visibility)"
                />
            </v-col>
            <v-col
                v-if="canFillData"
                class="px-1"
                cols="12"
            >
                <v-text-field
                    v-model.trim="form_data.api_secret"
                    :label="get_field_translations('api_secret')"
                    :error-messages="validation_errors.api_secret_error"
                    outlined
                    :append-icon="api_secret_visibility ? 'visibility' : 'visibility_off'"
                    :type="api_secret_visibility ? 'password' : 'text'"
                    class="pr-1"
                    @click:append="() => (api_secret_visibility = !api_secret_visibility)"
                />
            </v-col>

            <v-col
                v-if="canFillData"
                class="px-1"
                cols="6"
            >
                <v-btn
                    outlined
                    ml-0
                    color="primary"
                    :loading="loading"
                    @click="onRunTestClick()"
                >
                    {{ $t("Test connection") }}
                </v-btn>
            </v-col>
            <v-col
                v-if="canFillData"
                cols="6"
                class="text-right px-1"
                style="line-height: 48px;"
            >
                <a :href="HelpModule.getHelpLink('setup_integration')" target="_blank">
                    {{ $t("Read the manual") }}
                </a>
            </v-col>
        </v-row>

        <v-row no-gutters class="mt-3">
            <v-col v-if="canReloadData" class="px-1" cols="6">
                <v-btn outlined ml-0 color="primary" :loading="loading" @click="onReloadData()">
                    {{ $t("Reload data") }}
                </v-btn>
            </v-col>
        </v-row>

        <template slot="footer">
            <v-btn
                v-if="canFillData"
                :loading="loading"
                color="error"
                text
                depressed
                @click.native="onDeleteClick()"
            >
                {{ $t("Delete") }}
            </v-btn>
            <v-spacer/>
            <v-btn
                color="primary"
                text
                @click.native="closeDialog()"
            >
                {{ $t("Cancel") }}
            </v-btn>
            <v-btn
                v-if="canFillData"
                :loading="loading"
                color="primary"
                class="white--text"
                @click.native="onSaveKeysClick()"
            >
                {{ $t("Save") }}
            </v-btn>
        </template>
    </GeneralDialogTemplate>
</template>

<script>
import {$t} from "@/i18n";
import {reactive, onMounted, toRefs, computed, ref} from "@vue/composition-api";
import DialogModule from "../../store/modules/dialog";
import ErrorModule from "../../store/modules/error";
import AppModule from "../../store/modules/app";
import AccountModule from "../../store/modules/account";
import GeneralDialogTemplate from "./GeneralDialogTemplate";
import FeatureModule from "@/store/modules/feature";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import {APP_STORE_APP_TYPES} from "@/helpers/constants";
import MeritAktivaModule from "@/store/modules/merit_aktiva";
import {getRegionAppTitle} from "../../helpers/helpers";
import HelpModule from "@/modules/help/help"
import FeedbackNotificationModule from "@/store/modules/feedback_notification";
import axios from "axios";

export default {
    name: "IntegrationClientSettings",
    components: {
        GeneralDialogTemplate
    },
    props: {
        dialogIndex: {
            type: Number,
            default: null
        }
    },
    setup(props) {
        const {validation_errors} = ErrorModule();
        const {accounts_info} = AccountModule();
        const {api_countries: merit_aktiva_api_countries} = MeritAktivaModule()
        const {dialogs, dialog_action, closeDialog, setConfirmOptions, openConfirmDialog} = DialogModule();
        const dialogPayload = dialogs.value[props.dialogIndex].payload
        const {showFeedbackNotification} = FeedbackNotificationModule()
        const accountModule = AccountModule()
        const {handleErrorResponse} = AppModule();

        const loading = ref(false);
        const canFillData = ref(false);

        const {
            fetchAllApps,
            getClientSettings,
            saveClientSettings,
            deleteClientSettings,
            testClientSettings
        } = FeatureModule();

        const state = reactive({
            integrations: [],
            client_integrations: [],
            form_data: {
                feature_id: "",
                api_key: "",
                api_secret: "",
                api_country: ''
            }
        });


        const get_selected_integration = computed(() => {
            return state.integrations.find(integration => integration.feature_id === state.form_data.feature_id)
        })

        const show_country_input = computed(() => {
            let vendor_name = get_selected_integration.value.name
            return vendor_name === APP_STORE_APP_TYPES.MERIT_AKTIVA
        })

        const get_field_translations = function (field_name) {
            let vendor_name = get_selected_integration.value.name
            const translations = {
                [APP_STORE_APP_TYPES.MERIT_AKTIVA]: {
                    api_key: $t('API ID'),
                    api_secret: $t('API key'),
                },
                [APP_STORE_APP_TYPES.SMART_ACCOUNTS]: {
                    api_key: $t('Api key'),
                    api_secret: $t('Api secret'),
                }
            }
            return translations[vendor_name][field_name]
        }

        const integration_selected = feature_id => {
            state.form_data.api_key = ""
            state.form_data.api_secret = ""
            state.form_data.api_country = ""
            for (let i = 0; i < state.client_integrations.length; i++) {
                let ci = state.client_integrations[i];
                if (ci.feature_id === feature_id) {
                    state.form_data.feature_id = ci.feature_id
                    state.form_data.api_key = ci.api_key
                    state.form_data.api_secret = ci.api_secret
                    state.form_data.api_country = ci.api_country
                    break
                }
            }
            canFillData.value = true
        };

        const loadAccountIntegrations = function () {
            canFillData.value = false
            let companyId = dialogPayload.account_id
            let account_info = accounts_info.value.find(x => x.id === companyId);
            fetchAllApps({company_id: companyId})
                .then(resp => {
                    let integrations = [];
                    const enabled = [APP_STORE_APP_TYPES.MERIT_AKTIVA, APP_STORE_APP_TYPES.SMART_ACCOUNTS]
                    resp.forEach(app => {
                        if (accountModule.isFeatureEnabled(companyId, app.name, false)) {
                            if (enabled.includes(app.name)) {
                                integrations.push({
                                    id: app.id,
                                    name: app.name,
                                    title: getRegionAppTitle(app.name, account_info.region_code),
                                    feature_id: app.id,
                                });
                            }
                        }
                    });
                    state.integrations = integrations

                    let client_id = dialogPayload.client_id;
                    let feature_name = dialogPayload.feature_name;
                    getClientSettings(client_id, 0)
                        .then(function (client_integrations) {
                            state.client_integrations = client_integrations
                            if (feature_name) {
                                for (let i = 0; i < state.integrations.length; i++) {
                                    let x = state.integrations[i];
                                    if (x.name === feature_name) {
                                        integration_selected(x.feature_id)
                                        break
                                    }
                                }
                            }
                        });
                })
                .catch(ex => errorDialog.showError(ex));
        };

        const load_data = async () => {
            loadAccountIntegrations();
        };
        const onSaveKeysClick = async () => {
            loading.value = true;
            let selected_integration = state.integrations.filter(x => x.feature_id === state.form_data.feature_id)[0];
            let feature_id = selected_integration.feature_id;
            let client_id = dialogPayload.client_id;
            const success = await saveClientSettings(client_id, feature_id, state.form_data);
            loading.value = false;

            if (dialog_action["value"]) {
                dialog_action["value"](selected_integration, client_id, "add");
            }
            if (success) closeDialog();
        };

        const onRunTestClick = async () => {
            loading.value = true;
            let selected_integration = state.integrations.filter(x => x.feature_id === state.form_data.feature_id)[0];
            await testClientSettings(dialogPayload.client_id, selected_integration.feature_id, state.form_data);
            loading.value = false;
        };

        const delete_keys = async () => {
            loading.value = true;
            let selected_integration = state.integrations.filter(x => x.feature_id === state.form_data.feature_id)[0];
            let feature_id = selected_integration.feature_id;
            let client_id = dialogPayload.client_id;
            const success = await deleteClientSettings(client_id, feature_id);
            loading.value = false;

            if (dialog_action["value"]) {
                dialog_action["value"](selected_integration, client_id, "delete");
            }
            if (success) closeDialog();
        };
        const onDeleteClick = () => {
            setConfirmOptions({
                title: $t("Are you sure to delete this integration info?"),
                text: "",
                confirm_text: $t("Confirm"),
                cancel_text: $t("Cancel"),
                action: delete_keys
            });
            openConfirmDialog();
        };
        onMounted(() => {
            load_data();
        });

        const canReloadData = computed(() => {
            return dialogPayload.client_id
                && state.form_data
                && state.form_data.feature_id
                && state.form_data.api_key
                && state.form_data.api_secret
                && canFillData.value;
        })

        const reloadData = (companyId, featureId, period) => {
            loading.value = true;
            showFeedbackNotification($t("Uku will let you know when it is ready."))
            let data = {client_ids: [dialogPayload.client_id]}
            axios
                .post(`/${companyId}/reload_client_data/${featureId}/${period}`, data)
                .then(resp => {
                    loading.value = false;
                    closeDialog()
                })
                .catch(ex => {
                    loading.value = false;
                    handleErrorResponse(state.form_data.value, ex);
                });
        };

        const onReloadData = () => {
            const companyId = dialogPayload.account_id
            const featureId = state.form_data.feature_id
            const period = 12
            setConfirmOptions({
                title: $t('Reload data') + ' (12 ' + $t('months') + ')',
                text: $t("This will overwrite existing summary tasks new values."),
                confirm_text: $t("Overwrite"),
                cancel_text: $t("Cancel"),
                action: () => reloadData(companyId, featureId, period),
            });
            openConfirmDialog();
        };

        return {
            ...toRefs(state),
            merit_aktiva_api_countries,
            closeDialog,
            onDeleteClick,
            onSaveKeysClick,
            onRunTestClick,
            onReloadData,
            loading,
            validation_errors,
            integration_selected,
            api_key_visibility: String,
            api_secret_visibility: String,
            get_field_translations,
            show_country_input,
            HelpModule,
            canFillData,
            canReloadData,
        };
    }
};
</script>
